/**
 * ScoreGauge - Circular Gauge Component
 *
 * Displays score as a circular or semi-circular gauge.
 * Features:
 * - Circular or semi-circular gauge
 * - Score value in center
 * - Color gradient by score
 * - Animated transitions
 * - Multiple size variants
 *
 * @epic DC-523 Vendor Certification Program
 * @task DC-554 (Phase 4.5)
 */

import React, { useMemo } from 'react';

// ============================================================================
// TYPES
// ============================================================================

export type GaugeVariant = 'full' | 'semi' | 'mini';
export type GaugeSize = 'sm' | 'md' | 'lg' | 'xl';

export interface ScoreGaugeProps {
  score: number;
  maxScore?: number;
  variant?: GaugeVariant;
  size?: GaugeSize;
  showValue?: boolean;
  showLabel?: boolean;
  showPercentSign?: boolean;
  label?: string;
  sublabel?: string;
  animated?: boolean;
  strokeWidth?: number;
  className?: string;
}

// ============================================================================
// CONFIGURATION
// ============================================================================

const sizeConfig: Record<GaugeSize, {
  diameter: number;
  fontSize: string;
  labelSize: string;
  sublabelSize: string;
  defaultStrokeWidth: number;
}> = {
  sm: {
    diameter: 60,
    fontSize: '14px',
    labelSize: '10px',
    sublabelSize: '8px',
    defaultStrokeWidth: 4,
  },
  md: {
    diameter: 100,
    fontSize: '24px',
    labelSize: '12px',
    sublabelSize: '10px',
    defaultStrokeWidth: 6,
  },
  lg: {
    diameter: 140,
    fontSize: '32px',
    labelSize: '14px',
    sublabelSize: '12px',
    defaultStrokeWidth: 8,
  },
  xl: {
    diameter: 180,
    fontSize: '42px',
    labelSize: '16px',
    sublabelSize: '14px',
    defaultStrokeWidth: 10,
  },
};

function getScoreColor(score: number, maxScore: number): string {
  const percentage = (score / maxScore) * 100;
  if (percentage >= 90) return '#16a34a'; // Green - Platinum
  if (percentage >= 75) return '#ca8a04'; // Gold
  if (percentage >= 60) return '#6b7280'; // Silver
  if (percentage >= 50) return '#cd7f32'; // Bronze
  return '#dc2626'; // Red - Not certified
}

function getScoreGradient(score: number, maxScore: number): { start: string; end: string } {
  const percentage = (score / maxScore) * 100;
  if (percentage >= 90) return { start: '#22c55e', end: '#16a34a' }; // Green
  if (percentage >= 75) return { start: '#fbbf24', end: '#ca8a04' }; // Gold
  if (percentage >= 60) return { start: '#9ca3af', end: '#6b7280' }; // Silver
  if (percentage >= 50) return { start: '#d97706', end: '#cd7f32' }; // Bronze
  return { start: '#f87171', end: '#dc2626' }; // Red
}

// ============================================================================
// FULL CIRCLE GAUGE
// ============================================================================

const FullCircleGauge: React.FC<{
  score: number;
  maxScore: number;
  diameter: number;
  strokeWidth: number;
  animated: boolean;
  showValue: boolean;
  showLabel: boolean;
  showPercentSign: boolean;
  label?: string;
  sublabel?: string;
  sizeStyles: typeof sizeConfig.md;
}> = ({
  score,
  maxScore,
  diameter,
  strokeWidth,
  animated,
  showValue,
  showLabel,
  showPercentSign,
  label,
  sublabel,
  sizeStyles,
}) => {
  const radius = (diameter - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const percentage = Math.min(100, (score / maxScore) * 100);
  const offset = circumference - (percentage / 100) * circumference;
  const color = getScoreColor(score, maxScore);
  const gradientId = `gauge-gradient-${Math.random().toString(36).substr(2, 9)}`;
  const gradient = getScoreGradient(score, maxScore);

  return (
    <div style={{ position: 'relative', width: diameter, height: diameter }}>
      <svg width={diameter} height={diameter}>
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={gradient.start} />
            <stop offset="100%" stopColor={gradient.end} />
          </linearGradient>
        </defs>
        {/* Background circle */}
        <circle
          cx={diameter / 2}
          cy={diameter / 2}
          r={radius}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={strokeWidth}
        />
        {/* Progress circle */}
        <circle
          cx={diameter / 2}
          cy={diameter / 2}
          r={radius}
          fill="none"
          stroke={`url(#${gradientId})`}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${diameter / 2} ${diameter / 2})`}
          style={{
            transition: animated ? 'stroke-dashoffset 0.8s ease-out' : 'none',
          }}
        />
      </svg>
      {/* Center content */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
        }}
      >
        {showValue && (
          <div
            style={{
              fontSize: sizeStyles.fontSize,
              fontWeight: 700,
              color,
              lineHeight: 1,
            }}
          >
            {Math.round(score)}
            {showPercentSign && <span style={{ fontSize: '0.6em' }}>%</span>}
          </div>
        )}
        {showLabel && label && (
          <div
            style={{
              fontSize: sizeStyles.labelSize,
              color: '#6b7280',
              marginTop: '4px',
            }}
          >
            {label}
          </div>
        )}
        {sublabel && (
          <div
            style={{
              fontSize: sizeStyles.sublabelSize,
              color: '#9ca3af',
              marginTop: '2px',
            }}
          >
            {sublabel}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// SEMI-CIRCLE GAUGE
// ============================================================================

const SemiCircleGauge: React.FC<{
  score: number;
  maxScore: number;
  diameter: number;
  strokeWidth: number;
  animated: boolean;
  showValue: boolean;
  showLabel: boolean;
  showPercentSign: boolean;
  label?: string;
  sublabel?: string;
  sizeStyles: typeof sizeConfig.md;
}> = ({
  score,
  maxScore,
  diameter,
  strokeWidth,
  animated,
  showValue,
  showLabel,
  showPercentSign,
  label,
  sublabel,
  sizeStyles,
}) => {
  const radius = (diameter - strokeWidth) / 2;
  const halfCircumference = Math.PI * radius;
  const percentage = Math.min(100, (score / maxScore) * 100);
  const offset = halfCircumference - (percentage / 100) * halfCircumference;
  const color = getScoreColor(score, maxScore);
  const gradientId = `gauge-semi-gradient-${Math.random().toString(36).substr(2, 9)}`;
  const gradient = getScoreGradient(score, maxScore);
  const height = diameter / 2 + strokeWidth;

  return (
    <div style={{ position: 'relative', width: diameter, height }}>
      <svg width={diameter} height={height}>
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={gradient.start} />
            <stop offset="100%" stopColor={gradient.end} />
          </linearGradient>
        </defs>
        {/* Background arc */}
        <path
          d={`M ${strokeWidth / 2} ${height - strokeWidth / 2}
              A ${radius} ${radius} 0 0 1 ${diameter - strokeWidth / 2} ${height - strokeWidth / 2}`}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        {/* Progress arc */}
        <path
          d={`M ${strokeWidth / 2} ${height - strokeWidth / 2}
              A ${radius} ${radius} 0 0 1 ${diameter - strokeWidth / 2} ${height - strokeWidth / 2}`}
          fill="none"
          stroke={`url(#${gradientId})`}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={halfCircumference}
          strokeDashoffset={offset}
          style={{
            transition: animated ? 'stroke-dashoffset 0.8s ease-out' : 'none',
          }}
        />
      </svg>
      {/* Center content */}
      <div
        style={{
          position: 'absolute',
          bottom: '0',
          left: '50%',
          transform: 'translateX(-50%)',
          textAlign: 'center',
        }}
      >
        {showValue && (
          <div
            style={{
              fontSize: sizeStyles.fontSize,
              fontWeight: 700,
              color,
              lineHeight: 1,
            }}
          >
            {Math.round(score)}
            {showPercentSign && <span style={{ fontSize: '0.6em' }}>%</span>}
          </div>
        )}
        {showLabel && label && (
          <div
            style={{
              fontSize: sizeStyles.labelSize,
              color: '#6b7280',
              marginTop: '2px',
            }}
          >
            {label}
          </div>
        )}
        {sublabel && (
          <div
            style={{
              fontSize: sizeStyles.sublabelSize,
              color: '#9ca3af',
            }}
          >
            {sublabel}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// MINI GAUGE
// ============================================================================

const MiniGauge: React.FC<{
  score: number;
  maxScore: number;
  showValue: boolean;
}> = ({ score, maxScore, showValue }) => {
  const color = getScoreColor(score, maxScore);
  const size = 32;
  const strokeWidth = 3;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const percentage = Math.min(100, (score / maxScore) * 100);
  const offset = circumference - (percentage / 100) * circumference;

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
      <div style={{ position: 'relative', width: size, height: size }}>
        <svg width={size} height={size}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="#e5e7eb"
            strokeWidth={strokeWidth}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        </svg>
      </div>
      {showValue && (
        <span style={{ fontSize: '14px', fontWeight: 600, color }}>
          {Math.round(score)}%
        </span>
      )}
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const ScoreGauge: React.FC<ScoreGaugeProps> = ({
  score,
  maxScore = 100,
  variant = 'full',
  size = 'md',
  showValue = true,
  showLabel = false,
  showPercentSign = true,
  label,
  sublabel,
  animated = true,
  strokeWidth,
  className = '',
}) => {
  const sizeStyles = sizeConfig[size];
  const effectiveStrokeWidth = strokeWidth ?? sizeStyles.defaultStrokeWidth;

  // Clamp score to valid range
  const clampedScore = useMemo(
    () => Math.max(0, Math.min(maxScore, score)),
    [score, maxScore]
  );

  if (variant === 'mini') {
    return (
      <div className={`score-gauge score-gauge-mini ${className}`}>
        <MiniGauge score={clampedScore} maxScore={maxScore} showValue={showValue} />
      </div>
    );
  }

  if (variant === 'semi') {
    return (
      <div className={`score-gauge score-gauge-semi ${className}`}>
        <SemiCircleGauge
          score={clampedScore}
          maxScore={maxScore}
          diameter={sizeStyles.diameter}
          strokeWidth={effectiveStrokeWidth}
          animated={animated}
          showValue={showValue}
          showLabel={showLabel}
          showPercentSign={showPercentSign}
          label={label}
          sublabel={sublabel}
          sizeStyles={sizeStyles}
        />
      </div>
    );
  }

  return (
    <div className={`score-gauge score-gauge-full ${className}`}>
      <FullCircleGauge
        score={clampedScore}
        maxScore={maxScore}
        diameter={sizeStyles.diameter}
        strokeWidth={effectiveStrokeWidth}
        animated={animated}
        showValue={showValue}
        showLabel={showLabel}
        showPercentSign={showPercentSign}
        label={label}
        sublabel={sublabel}
        sizeStyles={sizeStyles}
      />
    </div>
  );
};

// ============================================================================
// TIER GAUGE VARIANT
// ============================================================================

export interface TierGaugeProps {
  score: number;
  tier: 'Platinum' | 'Gold' | 'Silver' | 'Bronze' | 'Not Certified';
  size?: GaugeSize;
  showTierLabel?: boolean;
  className?: string;
}

export const TierGauge: React.FC<TierGaugeProps> = ({
  score,
  tier,
  size = 'md',
  showTierLabel = true,
  className = '',
}) => {
  return (
    <ScoreGauge
      score={score}
      size={size}
      variant="full"
      showValue={true}
      showLabel={showTierLabel}
      label={tier}
      sublabel="AI Readiness"
      className={className}
    />
  );
};

export default ScoreGauge;
